import type { ChartConfig } from '../../../types';
import { CHART_STYLE } from './chartStyles';

interface KpiCardViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
}

/**
 * Humanize a column name: split on underscores/camelCase, capitalize words.
 * e.g. "TotalRepairCost" → "Total Repair Cost"
 *      "total_teu"       → "Total Teu"
 */
function humanizeLabel(name: string): string {
  return name
    // insert space before uppercase letters in camelCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // replace underscores with spaces
    .replace(/_/g, ' ')
    // capitalize first letter of each word
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format a number with locale-aware grouping and sensible decimals. */
function formatKpiValue(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  // Use up to 2 decimal places, but trim trailing zeros
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Renders single-row numeric ERAX results as prominent KPI metric cards.
 *
 * When ERAX returns a scalar result like `{ TotalRepairCost: 10407451.24 }`,
 * charts are meaningless — this component displays the value prominently
 * instead of leaving the Chart tab empty.
 *
 * When discrete (label) columns are present (e.g. { Port: "SG", TEU: 5000 }),
 * they are shown as a subtitle above the metric cards for context.
 */
export function KpiCardView({ data, config }: KpiCardViewProps) {
  if (!data || data.length === 0) return null;

  const row = data[0];

  // Extract numeric metric columns
  const metricNames: string[] = Array.isArray(config.yAxis)
    ? config.yAxis
    : config.yAxis
    ? [config.yAxis]
    : Object.keys(row).filter((k) => typeof row[k] === 'number');

  const metrics = metricNames
    .map((name) => {
      const raw = row[name];
      const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
      return { name, value };
    })
    .filter((m) => Number.isFinite(m.value));

  if (metrics.length === 0) return null;

  // Extract discrete label columns for context subtitle
  // (e.g. Port: "Singapore" when showing TEU for a specific port)
  const labelEntries: { name: string; value: string }[] = Object.entries(row)
    .filter(([key, val]) => typeof val === 'string' && !metricNames.includes(key))
    .map(([key, val]) => ({ name: key, value: String(val) }));

  const isSingle = metrics.length === 1;

  return (
    <div className={`flex flex-col items-center w-full ${isSingle ? 'py-8' : 'py-4'}`}>
      {/* Context subtitle from discrete columns */}
      {labelEntries.length > 0 && (
        <div className="mb-3 text-center">
          {labelEntries.map((entry) => (
            <span
              key={entry.name}
              className="inline-flex items-center gap-1 px-2 py-0.5 mx-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600"
            >
              <span className="text-gray-400">{humanizeLabel(entry.name)}:</span>
              <span className="font-medium">{entry.value}</span>
            </span>
          ))}
        </div>
      )}

      {/* Metric cards */}
      <div className="flex flex-wrap gap-3 justify-center items-stretch">
        {metrics.map((metric, idx) => (
          <div
            key={metric.name}
            className={`flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-sm ${
              isSingle ? 'px-10 py-8 min-w-[240px]' : 'px-6 py-5 min-w-[160px] flex-1 max-w-[260px]'
            }`}
          >
            <span
              className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2 text-center"
            >
              {humanizeLabel(metric.name)}
            </span>
            <span
              className={`font-semibold tabular-nums text-center ${
                isSingle ? 'text-3xl' : 'text-2xl'
              }`}
              style={{ color: CHART_STYLE.colors[idx % CHART_STYLE.colors.length] }}
            >
              {formatKpiValue(metric.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
